import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useProject } from '../context/ProjectContext';
import { API_URL, getVscodeBaseUrl } from '../config';

interface CodePanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Host filesystem folder for code-server (?folder=). Null = project default. */
  folder?: string | null;
  /** Optional subtitle (e.g. branch name) */
  label?: string | null;
}

const CodePanel: React.FC<CodePanelProps> = ({ isOpen, onClose, folder, label }) => {
  const { currentProject } = useProject();
  const [iframeKey, setIframeKey] = useState(0);
  const [booted, setBooted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspacePath, setWorkspacePath] = useState<string | null>(folder ?? null);
  const lastFolderRef = useRef<string | null | undefined>(undefined);
  const bootGenRef = useRef(0);

  const codeServerUrl = workspacePath
    ? `${getVscodeBaseUrl()}/?folder=${encodeURIComponent(workspacePath)}`
    : null;

  // Resolve host path as soon as we know the project (even while panel is closed)
  useEffect(() => {
    if (folder) {
      setWorkspacePath(folder);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/workspace/host-root`, {
          withCredentials: true,
        });
        const root = String(res.data?.host_root || '').replace(/\/$/, '');
        if (!root) throw new Error('WORKSPACE_HOST_PATH není nastavený na serveru');
        const path = currentProject?.github_repo
          ? `${root}/${currentProject.github_repo}`
          : root;
        if (!cancelled) setWorkspacePath(path);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || 'Nepodařilo se zjistit cestu k workspace');
          setWorkspacePath(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [folder, currentProject?.github_repo]);

  // Soft-boot once; remount iframe only when folder URL actually changes.
  // Use a generation counter so React Strict Mode / effect re-runs cannot
  // leave us stuck on "Připojuji…" after a cancelled first attempt.
  useEffect(() => {
    if (!codeServerUrl || !workspacePath) return;

    const folderChanged = lastFolderRef.current !== workspacePath;
    if (booted && !folderChanged) return;

    const gen = ++bootGenRef.current;

    const boot = async () => {
      try {
        await axios.post(`${API_URL}/api/auth/session-cookie`, {}, { withCredentials: true });
        if (bootGenRef.current !== gen) return;

        // Soft probe with timeout — do not block iframe forever
        const ctrl = new AbortController();
        const timer = window.setTimeout(() => ctrl.abort(), 8000);
        let probeOk = true;
        try {
          const probe = await fetch(codeServerUrl, {
            method: 'GET',
            credentials: 'include',
            signal: ctrl.signal,
          });
          if (probe.status === 401) {
            throw new Error('Nejsi přihlášený pro VS Code proxy — odhlas se a znovu přihlas.');
          }
          if (probe.status >= 500) {
            throw new Error(
              'VS Code server neodpovídá. Spusť: systemctl --user restart code-server'
            );
          }
        } catch (e: any) {
          if (e?.name === 'AbortError') {
            // Timed out — still show iframe; code-server may just be slow
            probeOk = true;
          } else {
            throw e;
          }
        } finally {
          window.clearTimeout(timer);
        }

        if (bootGenRef.current !== gen) return;
        if (folderChanged) setIframeKey((k) => k + 1);
        lastFolderRef.current = workspacePath;
        setError(null);
        if (probeOk) setBooted(true);
      } catch (e: any) {
        if (bootGenRef.current !== gen) return;
        setError(e?.message || 'Nepodařilo se připojit k VS Code');
        setBooted(false);
      }
    };

    void boot();
    // Intentionally omit `booted` — we gate with refs so reopen does not remount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeServerUrl, workspacePath]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const retry = () => {
    if (!codeServerUrl) {
      setError('Chybí cesta k workspace');
      return;
    }
    setError(null);
    setBooted(false);
    lastFolderRef.current = undefined;
    bootGenRef.current += 1;
    const gen = bootGenRef.current;
    axios
      .post(`${API_URL}/api/auth/session-cookie`, {}, { withCredentials: true })
      .then(async () => {
        const ctrl = new AbortController();
        const timer = window.setTimeout(() => ctrl.abort(), 8000);
        try {
          const probe = await fetch(codeServerUrl, {
            credentials: 'include',
            signal: ctrl.signal,
          });
          if (probe.status === 401) {
            throw new Error('Nejsi přihlášený pro VS Code proxy — odhlas se a znovu přihlas.');
          }
          if (probe.status >= 500) {
            throw new Error('VS Code server neodpovídá.');
          }
        } catch (e: any) {
          if (e?.name !== 'AbortError') throw e;
        } finally {
          window.clearTimeout(timer);
        }
        if (bootGenRef.current !== gen) return;
        setIframeKey((k) => k + 1);
        lastFolderRef.current = workspacePath;
        setBooted(true);
      })
      .catch((e) => {
        if (bootGenRef.current === gen) setError(e?.message || 'Retry failed');
      });
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <div
        className={`
          fixed top-0 left-0 h-screen z-50
          bg-slate-900
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}
          w-full lg:w-2/3
          border-r border-slate-700
          shadow-panel
        `}
        aria-hidden={!isOpen}
      >
        <div className="absolute top-0 left-0 right-0 z-10 px-3 py-1.5 text-[11px] font-mono
          bg-slate-950/95 text-slate-300 border-b border-slate-700 flex items-center gap-2 truncate">
          <span className="text-complic-300 font-semibold shrink-0">VS Code</span>
          <span className="truncate opacity-80">
            {label ? `${label} · ` : ''}
            {workspacePath}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto shrink-0 text-slate-400 hover:text-white px-1"
            title="Zavřít"
            tabIndex={isOpen ? 0 : -1}
          >
            ×
          </button>
        </div>

        <div className="w-full h-screen pt-7">
          {error ? (
            <div className="h-full flex items-center justify-center p-8 text-center">
              <div className="max-w-md space-y-3">
                <p className="text-slate-200 font-semibold">VS Code není dostupný</p>
                <p className="text-sm text-slate-400">{error}</p>
                <button
                  type="button"
                  onClick={retry}
                  className="px-3 py-1.5 rounded-lg bg-complic-600 hover:bg-complic-700 text-white text-sm"
                >
                  Zkusit znovu
                </button>
              </div>
            </div>
          ) : booted && codeServerUrl ? (
            <iframe
              key={iframeKey}
              src={codeServerUrl}
              className="w-full h-full border-0 bg-slate-900"
              title="VS Code Editor"
              allow="clipboard-read; clipboard-write"
            />
          ) : (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm">
              Připojuji VS Code…
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CodePanel;
